import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { formatDateTime, calculateImpactTime } from '../services/date';

const IncidentDetails = ({ inc, onPublish, onUnpublish, onEdit, isPublished, isPublishing, clusterAliasMap = {} }) => {
  const handleNavigation = (e) => {
    // Prevent button clicks from triggering navigation
    if (e.target.tagName === 'BUTTON') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === `#incident-${inc.id}`) {
        const element = document.getElementById(`incident-${inc.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    window.addEventListener('hashchange', checkHash, true);
    checkHash(); // Check on initial render

    return () => {
      window.removeEventListener('hashchange', checkHash, true);
    };
  }, [inc.id]);

  return (
    <a href={`#incident-${inc.id}`} onClick={handleNavigation} className="block py-4 -mx-4 px-4 hover:bg-gray-50 border-b border-gray-200">
      <div id={`incident-${inc.id}`} className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div className="flex-grow min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-black mt-2 mb-3">{inc.description}</p>
          <div className="text-sm text-gray-600 space-y-1">
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1">
              <span><strong>Began:</strong> {formatDateTime(inc.start_time)}</span>
              <span className="hidden sm:inline"> | </span>
              <span><strong>Created:</strong> {formatDateTime(inc.created)}</span>
              <span className="hidden sm:inline"> | </span>
              <span><strong>Ended:</strong> {formatDateTime(inc.end_time)}</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-1">
              <span><strong>Status:</strong> {inc.status}</span>
              <span className="hidden sm:inline"> | </span>
              <span><strong>Severity:</strong> {inc.severity}</span>
              {inc.status === 'resolved' && inc.end_time && calculateImpactTime(inc.created, inc.end_time) && (
                <>
                  <span className="hidden sm:inline"> | </span>
                  <span className="text-gray-500"><strong>Total Impact:</strong> {calculateImpactTime(inc.created, inc.end_time)}</span>
                </>
              )}
            </div>
            <div className="prose prose-sm max-w-full"><ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{inc.most_recent_update_text}</ReactMarkdown></div>
            {inc.affected_services && inc.affected_services.filter(s => s).length > 0 && (
              <p><strong>Affected Services:</strong> {inc.affected_services.filter(s => s).join(', ')}</p>
            )}
            {inc.affected_clusters && inc.affected_clusters.length > 0 && (
              <p><strong>Affected Clusters:</strong> {inc.affected_clusters.map(c => clusterAliasMap[c] || c).join(', ')}</p>
            )}
          </div>
        </div>
        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 sm:ml-6">
          {onPublish && !isPublished && (
            <button
              onClick={() => onPublish(inc)}
              disabled={isPublishing}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:bg-gray-400"
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          )}
          {onUnpublish && isPublished && (
            <button
              onClick={() => onUnpublish(inc)}
              disabled={isPublishing}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
            >
              {isPublishing ? 'Unpublishing...' : 'Unpublish'}
            </button>
          )}
          {onEdit && isPublished && (
            <button
              onClick={() => onEdit(inc)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    </a>
  );
};

export default function IncidentList({ incidents, publishedIncidents = [], onUnpublish, onEdit, publishingIncidentId, onIncidentClick, clusterAliasMap = {} }) {
  const publishedIncidentIds = new Set(publishedIncidents.map(inc => inc.id));
  const allIncidents = incidents;

  return (
    <div>
      {allIncidents.length === 0 ? (
        <p className="text-gray-500 italic">
          No incidents reported.
        </p>
      ) : (
        allIncidents.map((inc) => (
          <IncidentDetails
            key={inc.id}
            inc={inc}
            onPublish={onIncidentClick}
            onUnpublish={onUnpublish}
            onEdit={onEdit}
            isPublished={publishedIncidentIds.has(inc.id)}
            isPublishing={publishingIncidentId === inc.id}
            clusterAliasMap={clusterAliasMap}
          />
        ))
      )}
    </div>
  );
}